'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import * as React from 'react';

import { Drawer, DrawerContent, DrawerTrigger } from '@heidi/design-system/drawer';
import { useIsMobile } from '@heidi/utils';

interface BaseProps {
  children: React.ReactNode;
}

const PopoverDrawerModeContext = React.createContext<boolean | null>(null);

/**
 * Parts must agree with their root on popover-vs-drawer mode within a single
 * render — independent useIsMobile() instances can tear during a resize
 * across the breakpoint, making Radix throw "must be used within".
 */
const useIsMobileMode = (): boolean => {
  const fromRoot = React.useContext(PopoverDrawerModeContext);
  const isMobile = useIsMobile();
  return fromRoot ?? isMobile;
};

interface RootPopoverDrawerProps extends BaseProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  dismissible?: boolean;
}

interface PopoverDrawerProps extends BaseProps {
  className?: string;
  asChild?: true;
  side?: 'top' | 'right' | 'bottom' | 'left' | undefined;
  sideOffset?: number;
  align?: 'start' | 'center' | 'end' | undefined;
  alignOffset?: number;
  disabled?: boolean;
  hideCloseIcon?: boolean;
}

interface PopoverDrawerContentProps extends PopoverDrawerProps {
  onKeyDownCapture?: React.KeyboardEventHandler<HTMLDivElement>;
  onOpenAutoFocus?: (event: Event) => void;
  'aria-labelledby'?: string;
  onCloseAutoFocus?: (event: Event) => void;
}

const PopoverDrawer = ({ dismissible, children, ...props }: RootPopoverDrawerProps) => {
  const isMobile = useIsMobile();

  return (
    <PopoverDrawerModeContext.Provider value={isMobile}>
      {!isMobile ? (
        <Popover {...props}>{children}</Popover>
      ) : (
        <Drawer dismissible={dismissible} {...props}>
          {children}
        </Drawer>
      )}
    </PopoverDrawerModeContext.Provider>
  );
};

const PopoverDrawerTrigger = ({ className, children, ...props }: PopoverDrawerProps) => {
  const isMobile = useIsMobileMode();
  const PopoverDrawerTrigger = !isMobile ? PopoverTrigger : DrawerTrigger;

  return (
    <PopoverDrawerTrigger className={className} {...props}>
      {children}
    </PopoverDrawerTrigger>
  );
};

const PopoverDrawerContent = ({
  className,
  children,
  hideCloseIcon,
  ...props
}: PopoverDrawerContentProps) => {
  const isMobile = useIsMobileMode();
  const PopoverDrawerContent = !isMobile ? PopoverContent : DrawerContent;
  return (
    <PopoverDrawerContent hideCloseIcon={hideCloseIcon} className={className} {...props}>
      {children}
    </PopoverDrawerContent>
  );
};

export { PopoverDrawer, PopoverDrawerContent, PopoverDrawerTrigger };
